import {
  getContents,
  getRoomPlans as fetchRoomPlansFromFirebase,
} from "../../FIREBASECONFIG";
import {
  cachedFetch,
  invalidateResortCache,
  resortCacheKeys,
} from "./cache";
import {
  buildAvailabilitySnapshot,
  getDateRange,
} from "./availability.utils";

/** ─── Hotels ─────────────────────────────────────────────── */

export const fetchHotels = async () =>
  cachedFetch("hotels", resortCacheKeys.hotels(), () =>
    getContents("hotels")
  );

export const fetchHotelById = async (hotelId) => {
  if (!hotelId) return null;
  const hotels = await fetchHotels();
  return (hotels || []).find((h) => h.id === hotelId) || null;
};

/** ─── Hotel rooms (room types) ────────────────────────────── */

export const fetchHotelRooms = async () =>
  cachedFetch("hotelRooms", resortCacheKeys.hotelRooms(), () =>
    getContents("hotel_rooms")
  );

export const fetchHotelRoomsByHotelId = async (hotelId) => {
  const rooms = await fetchHotelRooms();
  if (!hotelId) return rooms || [];
  return (rooms || []).filter(
    (room) => String(room.hotel_id || "") === String(hotelId)
  );
};

/** ─── Room plans ─────────────────────────────────────────── */

export const fetchRoomPlans = async (roomId) => {
  if (!roomId) return [];
  return cachedFetch("roomPlans", resortCacheKeys.roomPlans(roomId), () =>
    fetchRoomPlansFromFirebase(roomId)
  );
};

export const fetchRoomPlansForRooms = async (rooms = []) =>
  Promise.all(
    (rooms || []).map(async (room) => {
      const plans = await fetchRoomPlans(room.id);
      return { ...room, plans: plans || [] };
    })
  );

/** ─── Reservations (availability input; short TTL) ────────── */

export const fetchReservations = async () =>
  cachedFetch("availability", resortCacheKeys.reservations(), () =>
    getContents("reservations")
  );

/** ─── Availability snapshot ───────────────────────────────── */

/**
 * Cached computed availability for a date range.
 * @param {{ startDate: string, endDate: string, hotelId?: string }} params
 */
export const fetchAvailability = async ({
  startDate,
  endDate,
  hotelId = "",
} = {}) => {
  const days = getDateRange(startDate, endDate);
  if (!days.length) {
    return { hotels: [], roomPlans: [], roomsCount: 0, days: [] };
  }

  const cacheKey = resortCacheKeys.availability({
    hotelId,
    startDate,
    endDate,
  });

  return cachedFetch("availability", cacheKey, async () => {
    const [hotelsData, roomsData, reservationsData] = await Promise.all([
      fetchHotels(),
      fetchHotelRooms(),
      fetchReservations(),
    ]);

    let candidateRooms = (roomsData || []).filter(
      (room) => room.isApproved === true
    );

    if (hotelId) {
      candidateRooms = candidateRooms.filter(
        (room) => String(room.hotel_id || "") === String(hotelId)
      );
    } else {
      const approvedHotelIds = new Set(
        (hotelsData || [])
          .filter((h) => h.isApproved === true)
          .map((h) => h.id)
      );
      candidateRooms = candidateRooms.filter((room) =>
        approvedHotelIds.has(room.hotel_id)
      );
    }

    const roomsWithPlans = await Promise.all(
      candidateRooms.map(async (room) => {
        const plans = await fetchRoomPlans(room.id);
        return {
          ...room,
          plans: (plans || []).filter((plan) => plan.stopSell !== true),
        };
      })
    );

    return buildAvailabilitySnapshot({
      hotelsData,
      roomsData,
      roomsWithPlans,
      reservationsData,
      days,
      scopedHotelId: hotelId,
    });
  });
};

/** Re-export invalidation so call sites don't dig into cache/ */
export { invalidateResortCache };
